<script setup>
import { ref, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useConfig } from 'dashboard/composables/useConfig';
import { useMapGetter } from 'dashboard/composables/store';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import wootConstants from 'dashboard/constants/globals';
import CapitaoAPI from 'dashboard/api/fandata/capitao';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';

// Painel MIT "perguntar ao Capitao". Substitui o painel enterprise (CopilotContainer)
// que so renderiza em build enterprise. Aberto pelo item "Perguntar ao Capitao" do
// menu ✨, que liga uiSettings.is_copilot_panel_open via useCopilotReply.
const { t } = useI18n();
// T-239: a resposta do Bira vinha com `**` aparecendo na tela. Ela é escrita em markdown, como
// toda saída de modelo, e o painel interpolava o texto cru. O mesmo formatador que o painel do
// Captain nativo usa resolve, e ele já sanitiza: virar negrito de verdade é melhor do que
// mandar o modelo não usar marcação, que é pedir para a resposta ficar sem hierarquia nenhuma.
const { formatMessage } = useMessageFormatter();
const { uiSettings, updateUISettings } = useUISettings();
const { isEnterprise } = useConfig();
const { width: windowWidth } = useWindowSize();

const currentChat = useMapGetter('getSelectedChat');
const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

// O painel enterprise so aparece em build enterprise com Captain ligado; nesse caso
// cedemos a ele para nao duplicar. Em community (nosso caso) o nosso sempre vale.
const enterprisePanelActive = computed(() => {
  if (!isEnterprise) return false;
  return isFeatureEnabledonAccount.value(
    currentAccountId.value,
    FEATURE_FLAGS.CAPTAIN
  );
});

const isOpen = computed(
  () => uiSettings.value?.is_copilot_panel_open && !enterprisePanelActive.value
);

const messages = ref([]);
const question = ref('');
const isLoading = ref(false);
const listRef = ref(null);

const scrollToBottom = async () => {
  await nextTick();
  if (listRef.value) listRef.value.scrollTop = listRef.value.scrollHeight;
};

const close = () => {
  updateUISettings({ is_copilot_panel_open: false });
};

const send = async () => {
  const text = question.value.trim();
  if (!text || isLoading.value) return;

  messages.value.push({ role: 'user', content: text });
  question.value = '';
  isLoading.value = true;
  await scrollToBottom();

  try {
    const { data } = await CapitaoAPI.ask(currentChat.value?.id, text);
    messages.value.push({
      role: 'capitao',
      content: data.answer || t('CAPITAO.NO_ANSWER'),
    });
  } catch (error) {
    messages.value.push({
      role: 'capitao',
      content: error?.response?.data?.error || t('CAPITAO.ERROR'),
      isError: true,
    });
  } finally {
    isLoading.value = false;
    await scrollToBottom();
  }
};
</script>

<template>
  <div
    v-if="isOpen"
    v-on-click-outside="() => (isSmallScreen ? close() : null)"
    class="bg-n-surface-2 h-full overflow-hidden flex flex-col fixed top-0 ltr:right-0 rtl:left-0 z-40 w-full max-w-sm transition-transform duration-300 ease-in-out md:static md:w-[320px] md:min-w-[320px] ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[360px] 2xl:w-[360px] shadow-lg md:shadow-none"
  >
    <header
      class="flex items-center justify-between flex-shrink-0 h-12 px-4 border-b border-n-weak"
    >
      <div class="flex items-center gap-2 font-medium text-n-slate-12">
        <fluent-icon icon="chat" size="16" class="text-n-violet-9" />
        <span>{{ t('CAPITAO.TITLE') }}</span>
      </div>
      <button
        class="flex items-center justify-center w-6 h-6 rounded-md text-n-slate-11 hover:bg-n-alpha-2"
        @click="close"
      >
        <fluent-icon icon="dismiss" size="16" />
      </button>
    </header>

    <div ref="listRef" class="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
      <p v-if="!messages.length" class="text-sm text-n-slate-11">
        {{ t('CAPITAO.EMPTY_STATE') }}
      </p>
      <div
        v-for="(msg, index) in messages"
        :key="index"
        class="flex flex-col"
        :class="msg.role === 'user' ? 'items-end' : 'items-start'"
      >
        <div
          class="max-w-[90%] rounded-lg px-3 py-2 text-sm [overflow-wrap:break-word]"
          :class="{
            'bg-n-violet-3 text-n-slate-12 whitespace-pre-wrap':
              msg.role === 'user',
            'bg-n-solid-3 text-n-slate-12 prose prose-sm prose-bubble':
              msg.role === 'capitao' && !msg.isError,
            'bg-n-ruby-3 text-n-ruby-11 whitespace-pre-wrap': msg.isError,
          }"
        >
          <!-- A pergunta de quem digitou vai crua: ela não é markdown, e formatar o que a
               pessoa escreveu mudaria o texto dela. Só a resposta do Bira é renderizada. -->
          <div
            v-if="msg.role === 'capitao' && !msg.isError"
            v-html="formatMessage(msg.content)"
          />
          <template v-else>{{ msg.content }}</template>
        </div>
      </div>
      <p v-if="isLoading" class="text-sm text-n-slate-11">
        {{ t('CAPITAO.THINKING') }}
      </p>
    </div>

    <footer class="flex-shrink-0 p-3 border-t border-n-weak">
      <div class="flex items-end gap-2">
        <textarea
          v-model="question"
          rows="1"
          :placeholder="t('CAPITAO.PLACEHOLDER')"
          class="flex-1 resize-none rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:border-n-violet-7"
          @keydown.enter.exact.prevent="send"
        />
        <button
          class="flex items-center justify-center w-9 h-9 rounded-lg bg-n-violet-9 text-white disabled:opacity-50"
          :disabled="isLoading || !question.trim()"
          @click="send"
        >
          <fluent-icon icon="send" size="16" />
        </button>
      </div>
    </footer>
  </div>
  <template v-else />
</template>
